import { useCallback } from 'react';
import { useSessionsStore } from '@/stores/sessions-store';
import { useExtensionUtil, hasExtension } from '@/hooks/useExtensionUtil';
import { extensionSendCBMessage } from '@/utils/extensionSendCBMessage';
import {
  fetchWorkspaceSessionsFromApi,
  loadSessionsViaExtensionOrApi,
  type SendCBMessage,
} from '@/utils/extensionSessions';

async function fetchSessionsIntoStore(
  sendCBMessage: SendCBMessage
): Promise<void> {
  const {
    setAllSessions,
    setSessionsLoaded,
    setActiveSessionId,
    setActiveSessionIds,
    setSessionsLoadInFlight,
  } = useSessionsStore.getState();

  const ext = await hasExtension();
  const hasExt = ext || window.troveApp?.isExtension?.();

  try {
    if (!hasExt) {
      const workspaceSessions = await fetchWorkspaceSessionsFromApi();
      setAllSessions(workspaceSessions);
      setSessionsLoaded(true);
      return;
    }

    const { sessions, csid, activeSessions } = await loadSessionsViaExtensionOrApi(sendCBMessage);
    if (csid) setActiveSessionId(csid);
    if (activeSessions?.length) {
      setActiveSessionIds(activeSessions);
    } else if (csid) {
      setActiveSessionIds([csid]);
    }
    setAllSessions(sessions);
    setSessionsLoaded(true);
  } catch (err) {
    console.error('[useLoadSessions] Failed to load sessions:', err);
    setSessionsLoaded(true);
  } finally {
    setSessionsLoadInFlight(null);
  }
}

/** Called after workspace account switch — bypasses sessionsLoaded guard. */
export async function reloadSessionsAfterAccountSwitch(): Promise<void> {
  const { sessionsLoadInFlight, setSessionsLoadInFlight } = useSessionsStore.getState();
  if (sessionsLoadInFlight) {
    await sessionsLoadInFlight;
    return;
  }
  const promise = fetchSessionsIntoStore(extensionSendCBMessage);
  setSessionsLoadInFlight(promise);
  await promise;
}

/**
 * Returns a `loadSessions` function that fetches workspace sessions from the API
 * and (when the extension is present) merges in unsaved sessions from the extension.
 *
 * Safe to call from any component inside ProtectedRoute — auth is guaranteed there.
 * Uses `sessionsLoaded` and `sessionsLoadInFlight` so multiple callers share one request.
 */
export function useLoadSessions() {
  const {
    sessionsLoaded,
    sessionsLoadInFlight,
    setSessionsLoadInFlight,
  } = useSessionsStore();
  const { sendCBMessage } = useExtensionUtil();

  const loadSessions = useCallback(async () => {
    if (sessionsLoaded) return;
    if (sessionsLoadInFlight) {
      await sessionsLoadInFlight;
      return;
    }

    const promise = fetchSessionsIntoStore(sendCBMessage);
    setSessionsLoadInFlight(promise);
    await promise;
  }, [
    sessionsLoaded,
    sessionsLoadInFlight,
    sendCBMessage,
    setSessionsLoadInFlight,
  ]);

  return { loadSessions, sessionsLoaded };
}
